/**
 * useRoomSocket -- connects to the calls worker's RoomDO via WebSocket and
 * mediates shared room state with peers.
 *
 *   Incoming: {type:'roomState', state:{users:[{id,name,avatarUrl,tracks,micEnabled,videoEnabled}]}}
 *   Outgoing: {type:'userUpdate', user:{...}} | {type:'heartbeat'} | {type:'leave'}
 *
 * `users` excludes the caller. Reconnects with exponential backoff on an
 * unexpected mid-call drop and re-announces presence on each open (so peers
 * re-learn our track ids); a handshake rejection or deliberate leave does not
 * reconnect. Ported from batesky-electron cfcalls (reliability-fixed version),
 * typed + swapped to the worker WS base and service-auth `?token=`.
 */

import {useEffect, useRef, useState} from 'react'

import {cfcallsConfig} from '../config'

export interface RoomUser {
  id: string
  name?: string
  avatarUrl?: string
  tracks?: {audio?: string; video?: string}
  micEnabled?: boolean
  videoEnabled?: boolean
}

export interface OutgoingUser {
  id: string
  name?: string
  avatarUrl?: string
  tracks: {audio?: string; video?: string}
  micEnabled: boolean
  videoEnabled: boolean
}

type Status = 'connecting' | 'connected' | 'reconnecting'

export function useRoomSocket({
  roomSlug,
  token,
  selfUserId,
  selfName,
}: {
  roomSlug: string
  /** Service-auth JWT (guest did:key or real user). */
  token: string | null
  selfUserId: string
  selfName?: string
}): {
  otherUsers: RoomUser[]
  connected: boolean
  status: Status
  joinError: string | null
  sendUserUpdate: (u: {
    tracks: {audio?: string; video?: string}
    micEnabled: boolean
    videoEnabled: boolean
    avatarUrl?: string
  }) => void
} {
  const [otherUsers, setOtherUsers] = useState<RoomUser[]>([])
  const [connected, setConnected] = useState(false)
  const [status, setStatus] = useState<Status>('connecting')
  const [joinError, setJoinError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const lastUpdateRef = useRef('')
  const pendingUpdateRef = useRef<OutgoingUser | null>(null)

  useEffect(() => {
    if (!roomSlug || !token || !selfUserId) return

    const params = new URLSearchParams({token})
    if (selfName) params.set('name', selfName)
    const url = `${cfcallsConfig.roomWsBase}/${encodeURIComponent(
      roomSlug,
    )}/ws?${params.toString()}`

    let cancelled = false
    let attempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let heartbeat: ReturnType<typeof setInterval> | null = null

    function scheduleReconnect() {
      if (cancelled) return
      const base = Math.min(1000 * 2 ** attempt, 15000)
      const jitter = base * 0.2 * (Math.random() - 0.5)
      attempt += 1
      setStatus('reconnecting')
      reconnectTimer = setTimeout(
        () => {
          if (!cancelled) connect()
        },
        Math.round(base + jitter),
      )
    }

    function flushPendingUpdate(ws: WebSocket) {
      const user = pendingUpdateRef.current
      if (!user || ws.readyState !== WebSocket.OPEN) return
      const next = JSON.stringify(user)
      if (next === lastUpdateRef.current) return
      lastUpdateRef.current = next
      ws.send(JSON.stringify({type: 'userUpdate', user}))
    }

    function connect() {
      if (cancelled) return
      let ws: WebSocket
      try {
        ws = new WebSocket(url)
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws
      let openedOnce = false
      // Fresh socket: the DO's attachment for us is empty, so force a resend.
      lastUpdateRef.current = ''

      ws.addEventListener('open', () => {
        openedOnce = true
        attempt = 0
        setConnected(true)
        setStatus('connected')
        setJoinError(null)
        flushPendingUpdate(ws)
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({type: 'heartbeat'}))
          }
        }, 10000)
      })

      ws.addEventListener('close', () => {
        setConnected(false)
        if (heartbeat) {
          clearInterval(heartbeat)
          heartbeat = null
        }
        if (cancelled) return
        if (!openedOnce) {
          // Handshake rejected (room full / bad auth) -- don't retry.
          setStatus('reconnecting')
          setJoinError('join_failed')
          return
        }
        scheduleReconnect()
      })

      ws.addEventListener('error', () => {
        // `close` owns reconnection; nothing to do here.
      })

      ws.addEventListener('message', evt => {
        let msg: {type?: string; state?: {users?: RoomUser[]}}
        try {
          msg = JSON.parse(evt.data)
        } catch {
          return
        }
        if (!msg || msg.type !== 'roomState') return
        const users = (msg.state && msg.state.users) || []
        setOtherUsers(users.filter(u => u.id !== selfUserId))
      })
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (heartbeat) clearInterval(heartbeat)
      const ws = wsRef.current
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({type: 'leave'}))
        }
        if (ws) ws.close()
      } catch {
        // already closing
      }
      wsRef.current = null
    }
  }, [roomSlug, token, selfUserId, selfName])

  function sendUserUpdate({
    tracks,
    micEnabled,
    videoEnabled,
    avatarUrl,
  }: {
    tracks: {audio?: string; video?: string}
    micEnabled: boolean
    videoEnabled: boolean
    avatarUrl?: string
  }) {
    pendingUpdateRef.current = {
      id: selfUserId,
      name: selfName,
      avatarUrl,
      tracks,
      micEnabled,
      videoEnabled,
    }
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      const next = JSON.stringify(pendingUpdateRef.current)
      if (next !== lastUpdateRef.current) {
        lastUpdateRef.current = next
        ws.send(
          JSON.stringify({type: 'userUpdate', user: pendingUpdateRef.current}),
        )
      }
    }
  }

  return {otherUsers, connected, status, joinError, sendUserUpdate}
}
